import { request, requestNoContent } from './client';

/**
 * Một trang dữ liệu do máy chủ trả về.
 *
 * Dùng chung cho mọi danh sách của hệ thống. Máy chủ luôn trả tổng số bản ghi, để giao diện hiển thị
 * được "1–25 trong 1.284" — với người làm kho hay kế toán thì con số tổng cho biết bộ lọc vừa đặt có
 * đúng hay không, trước khi họ bắt đầu đọc từng dòng.
 */
export interface PagedResponse<TItem> {
  readonly items: readonly TItem[];
  readonly totalCount: number;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly totalPageCount: number;
}

/**
 * Loại đơn vị đo.
 *
 * Giá trị số phải khớp với phía máy chủ. Không dùng chuỗi tên loại: đổi câu chữ tiếng Việt về sau
 * sẽ làm dữ liệu cũ thành vô nghĩa.
 */
export const UnitOfMeasureType = {
  /** Đếm — cái, bộ, hộp, cuộn, lô. */
  Count: 1,

  /** Chiều dài — mét, xentimét, milimét. */
  Length: 2,

  /** Khối lượng — kilôgam, gam. */
  Mass: 3,

  /** Thể tích — lít. */
  Volume: 4,

  /** Thời gian — giờ, ngày, tháng. */
  Time: 5,

  /** Diện tích — mét vuông. */
  Area: 6,

  /** Bộ hoặc cụm. */
  Assembly: 7,
} as const;

/** Giá trị của loại đơn vị đo. */
export type UnitOfMeasureTypeValue =
  (typeof UnitOfMeasureType)[keyof typeof UnitOfMeasureType];

/** Tên tiếng Việt của từng loại đơn vị, dùng để hiển thị. */
export const unitOfMeasureTypeNames: Readonly<Record<UnitOfMeasureTypeValue, string>> = {
  [UnitOfMeasureType.Count]: 'Đếm',
  [UnitOfMeasureType.Length]: 'Chiều dài',
  [UnitOfMeasureType.Mass]: 'Khối lượng',
  [UnitOfMeasureType.Volume]: 'Thể tích',
  [UnitOfMeasureType.Time]: 'Thời gian',
  [UnitOfMeasureType.Area]: 'Diện tích',
  [UnitOfMeasureType.Assembly]: 'Bộ / Cụm',
};

/** Một đơn vị đo. */
export interface UnitOfMeasure {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly nameEn: string;
  readonly unitOfMeasureType: UnitOfMeasureTypeValue;
  readonly baseUnitId: string | null;
  readonly baseUnitCode: string | null;
  readonly decimalPlaces: number;
  readonly isSystemDefined: boolean;
  readonly isActive: boolean;
  readonly sortOrder: number;
}

/** Điều kiện lọc danh sách đơn vị đo. */
export interface UnitOfMeasureFilter {
  readonly searchTerm?: string;
  readonly unitOfMeasureType?: UnitOfMeasureTypeValue;
  readonly includeInactive?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung gửi lên khi thêm đơn vị đo. */
export interface CreateUnitOfMeasurePayload {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string;
  readonly unitOfMeasureType: UnitOfMeasureTypeValue;
  readonly decimalPlaces: number;
  readonly baseUnitId: string | null;
  readonly sortOrder: number;
}

/** Nội dung gửi lên khi sửa đơn vị đo. Không có mã và loại: cả hai không sửa được. */
export interface UpdateUnitOfMeasurePayload {
  readonly name: string;
  readonly nameEn: string;
  readonly decimalPlaces: number;
  readonly baseUnitId: string | null;
  readonly sortOrder: number;
}

const baseRoute = '/api/v1/platform/units-of-measure';

/**
 * Dựng phần truy vấn của địa chỉ.
 *
 * Bỏ hẳn tham số rỗng thay vì gửi chuỗi trống: máy chủ phân biệt "không lọc" với "lọc theo chuỗi
 * trống", và gửi chuỗi trống sẽ thành điều kiện lọc thật.
 */
function buildQueryString(filter: UnitOfMeasureFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.unitOfMeasureType !== undefined) {
    parameters.set('unitOfMeasureType', String(filter.unitOfMeasureType));
  }

  if (filter.includeInactive === true) {
    parameters.set('includeInactive', 'true');
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', String(filter.pageNumber));
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', String(filter.pageSize));
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách đơn vị đo. */
export async function listUnitsOfMeasure(
  filter: UnitOfMeasureFilter,
): Promise<PagedResponse<UnitOfMeasure>> {
  return await request<PagedResponse<UnitOfMeasure>>(
    `${baseRoute}${buildQueryString(filter)}`,
  );
}

/** Thêm một đơn vị đo. */
export async function createUnitOfMeasure(
  payload: CreateUnitOfMeasurePayload,
): Promise<UnitOfMeasure> {
  return await request<UnitOfMeasure>(baseRoute, { method: 'POST', body: payload });
}

/** Sửa một đơn vị đo. */
export async function updateUnitOfMeasure(
  id: string,
  payload: UpdateUnitOfMeasurePayload,
): Promise<UnitOfMeasure> {
  return await request<UnitOfMeasure>(`${baseRoute}/${id}`, { method: 'PUT', body: payload });
}

/** Ngừng sử dụng hoặc mở lại một đơn vị đo. */
export async function setUnitOfMeasureActive(
  id: string,
  isActive: boolean,
): Promise<UnitOfMeasure> {
  return await request<UnitOfMeasure>(`${baseRoute}/${id}/active`, {
    method: 'PUT',
    body: { isActive },
  });
}

/** Xóa một đơn vị đo. */
export async function deleteUnitOfMeasure(id: string): Promise<void> {
  await requestNoContent(`${baseRoute}/${id}`, { method: 'DELETE' });
}
